import React, { useEffect, useMemo, useState } from "react";
import { ordersApi, accountsApi, customersApi, expensesApi } from "../api";
import { useI18n } from "../i18n";
import { showToast } from "../utils/toast";

export default function OrderModal({ open, onClose, orderId }) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({ name: "", date: today, sellingPrice: "", customLines: [], customerId: "", customerName: "", incomeCategory: "" });
  const [customers, setCustomers] = useState([]);
  const [incomeCats, setIncomeCats] = useState([]);
  const [showCatList, setShowCatList] = useState(false);
  const [catQuery, setCatQuery] = useState("");
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");

  const loadOrder = async () => {
    if (!orderId) return;
    try {
      const o = await ordersApi.detail(orderId);
      setForm({
        name: o?.name || "",
        date: (o?.date?._seconds) ? new Date(o.date._seconds * 1000).toISOString().split('T')[0] : today,
        sellingPrice: String(o?.sellingPrice || ""),
        customLines: (o?.customLines || []).map((l)=>({ name: l.name || "", quantity: l.quantity || 0, price: l.price || 0, manualPrice: true })),
        customerId: o?.customerId || "",
        customerName: o?.customerName || "",
        incomeCategory: o?.incomeCategory || "",
      });
      setSelectedAccountId(o?.money_account_id || "");
      try {
        const cname = (customers.find(c => c.customerId === (o?.customerId || '')) || {}).name || (o?.customerName || '');
        if (cname) setCustomerQuery(cname);
      } catch {}
      try {
        const icat = String(o?.incomeCategory || '');
        if (icat) setCatQuery(icat);
      } catch {}
    } catch {}
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [accs, custs, cats] = await Promise.all([
          accountsApi.list().catch(()=>[]),
          customersApi.list().catch(()=>[]),
          expensesApi.listUnified('income').catch(()=>({ defaults: ['Sales','Services','Other'], customs: [] })),
        ]);
        setAccounts(Array.isArray(accs) ? accs : []);
        setCustomers(Array.isArray(custs) ? custs : []);
        try {
          const cname = (custs.find(c => c.customerId === form.customerId) || {}).name || form.customerName || '';
          if (cname) setCustomerQuery(cname);
        } catch {}
        if (Array.isArray(cats)) setIncomeCats(cats);
        else if (cats && typeof cats === 'object') {
          const defs = Array.isArray(cats.defaults) ? cats.defaults : [];
          const customs = Array.isArray(cats.customs) ? cats.customs : [];
          setIncomeCats(Array.from(new Set([ ...defs, ...customs ])));
        }
      } catch {}
      await loadOrder();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  const totalCost = useMemo(() => {
    return (form.customLines || []).reduce((s, l) => s + (Number(l.price) || 0) * (Number(l.quantity) || 0), 0);
  }, [form.customLines]);

  const setLine = (idx, field, value) => {
    const lines = [...(form.customLines || [])];
    const next = { ...lines[idx], [field]: value };
    lines[idx] = next;
    setForm({ ...form, customLines: lines });
  };

  const addLine = () => setForm({ ...form, customLines: [...(form.customLines || []), { name: "", quantity: 1, price: 0, manualPrice: true }] });
  const removeLine = (idx) => {
    const lines = [...(form.customLines || [])];
    lines.splice(idx, 1);
    setForm({ ...form, customLines: lines });
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const payload = {
        name: form.name,
        date: form.date,
        sellingPrice: form.sellingPrice,
        customLines: form.customLines,
        customerId: form.customerId || "",
        customerName: (form.customerId ? ((customers.find(c=>c.customerId===form.customerId)||{}).name || "") : ""),
        incomeCategory: form.incomeCategory || "",
        money_account_id: selectedAccountId === '' ? null : (selectedAccountId || undefined),
      };
      if (orderId) await ordersApi.update(orderId, payload);
      else await ordersApi.create(payload);
      if (onClose) onClose(true);
    } catch (e) {
      console.error('Failed to save order', e);
      showToast({ message: String(e?.message || e), variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-green-900/10">
      <div className="w-full sm:w-[560px] bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-green-100 max-h-[90vh] overflow-auto">
        <div className="p-4 border-b border-green-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-800">{orderId ? t('orders.form.editTitle') : t('orders.form.newTitle')}</div>
          <button className="text-sm text-gray-500" onClick={()=>onClose && onClose(false)}>✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input className="w-full border rounded px-3 py-2" placeholder={t('orders.form.orderName.placeholder')} value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} />
            <input className="w-full border rounded px-3 py-2" type="number" placeholder={t('orders.form.sellingPrice.placeholder')} value={form.sellingPrice} onChange={(e)=>setForm({...form, sellingPrice: e.target.value})} />
            <input className="w-full border rounded px-3 py-2" type="date" value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})} />
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Customer</div>
            <div className="relative">
              <input
                className="w-full border rounded px-3 py-2"
                placeholder="Select or type customer"
                value={customerQuery}
                onChange={(e)=>{ setCustomerQuery(e.target.value); setShowCustomerList(true); }}
                onFocus={()=> setShowCustomerList(true)}
                onBlur={()=> setTimeout(()=> {
                  setShowCustomerList(false);
                  const q = String(customerQuery||'').trim().toLowerCase();
                  const match = (customers||[]).find(c => String(c.name||'').toLowerCase() === q);
                  if (!match) {
                    setForm(prev => ({ ...prev, customerId: '', customerName: '' }));
                    setCustomerQuery('');
                  }
                }, 150)}
              />
              {showCustomerList && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-green-100 rounded-lg shadow-lg max-h-56 overflow-auto">
                  {(() => {
                    const q = String(customerQuery||'').trim().toLowerCase();
                    const list = (customers||[]).filter(c => !q || String(c.name||'').toLowerCase().includes(q));
                    if (list.length === 0) {
                      return (
                        <div className="p-2 text-sm text-gray-600">
                          <div className="mb-2">No customers found</div>
                          <button className="text-green-700 underline" onMouseDown={(e)=> e.preventDefault()} onClick={()=>{ try { if (window && window.tpNavigate) window.tpNavigate('customers'); } catch {}; if (onClose) onClose(false); }}>+ Add Customer</button>
                        </div>
                      );
                    }
                    return list.map(c => (
                      <button
                        key={c.customerId}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-green-50"
                        onMouseDown={(e)=> e.preventDefault()}
                        onClick={()=>{ setForm(prev => ({...prev, customerId: c.customerId, customerName: c.name})); setCustomerQuery(c.name); setShowCustomerList(false); }}
                      >{c.name}</button>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Category (Income)</div>
            <div className="relative">
              <input
                className="w-full border rounded px-3 py-2"
                placeholder="Select or type category"
                value={catQuery}
                onChange={(e)=>{ setCatQuery(e.target.value); setShowCatList(true); }}
                onFocus={()=> setShowCatList(true)}
                onBlur={()=> setTimeout(()=> {
                  setShowCatList(false);
                  const q = String(catQuery||'').trim().toLowerCase();
                  const match = (incomeCats||[]).find(c => String(c||'').toLowerCase() === q);
                  if (!match) {
                    setForm(prev => ({ ...prev, incomeCategory: '' }));
                    setCatQuery('');
                  } else {
                    setForm(prev => ({ ...prev, incomeCategory: match }));
                  }
                }, 150)}
              />
              {showCatList && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-green-100 rounded-lg shadow-lg max-h-56 overflow-auto">
                  {(() => {
                    const q = String(catQuery||'').trim().toLowerCase();
                    const list = (incomeCats||[]).filter(c => !q || String(c||'').toLowerCase().includes(q));
                    if (list.length === 0) {
                      return (
                        <div className="p-2 text-sm text-gray-600">
                          <div className="mb-2">No categories found</div>
                          <button className="text-green-700 underline" onMouseDown={(e)=> e.preventDefault()} onClick={()=>{ try { if (window && window.tpNavigate) window.tpNavigate('categories'); } catch {}; if (onClose) onClose(false); }}>+ Add Category</button>
                        </div>
                      );
                    }
                    return list.map(c => (
                      <button
                        key={c}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-green-50"
                        onMouseDown={(e)=> e.preventDefault()}
                        onClick={()=>{ setForm(prev => ({...prev, incomeCategory: c})); setCatQuery(c); setShowCatList(false); }}
                      >{c}</button>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Account</div>
            <select className="w-full border rounded px-3 py-2" value={selectedAccountId} onChange={(e)=>setSelectedAccountId(e.target.value)}>
              <option value="">Select account (clears selection)</option>
              {(accounts||[]).map((a)=> (<option key={a.accountId} value={a.accountId}>{a.name}</option>))}
            </select>
          </div>
          <div>
            <div className="text-sm font-semibold mb-1">{t('orders.form.costItems.label')}</div>
            {(form.customLines||[]).map((l, idx) => (
              <div className="grid grid-cols-12 gap-2 mb-2 items-center" key={idx}>
                <input className="col-span-5 border px-2 py-2 rounded" placeholder={t('orders.form.costItems.col.name')} value={l.name} onChange={(e)=>setLine(idx,'name', e.target.value)} />
                <input className="col-span-2 border px-2 py-2 rounded" type="number" placeholder={t('orders.form.costItems.col.qty')} value={l.quantity} onChange={(e)=>setLine(idx,'quantity', e.target.value)} />
                <input className="col-span-3 border px-2 py-2 rounded" type="number" placeholder={t('orders.form.costItems.col.price')} value={l.price} onChange={(e)=>setLine(idx,'price', e.target.value)} />
                <button className="col-span-2 text-red-600 text-xs underline" onClick={()=>removeLine(idx)}>{t('orders.recent.delete')}</button>
              </div>
            ))}
            <button className="text-sm text-green-600 underline" onClick={addLine}>{t('orders.form.addLine')}</button>
          </div>
          <div className="text-sm">
            <strong>{t('orders.form.totalCost')}: </strong>₹{Number(totalCost).toFixed(2)}
          </div>
        </div>
        <div className="p-4 flex gap-2 border-t border-green-100">
          <button className={`flex-1 bg-green-600 text-white rounded-xl py-2 text-sm shadow ${saving?'opacity-60':''}`} disabled={saving} onClick={handleSave}>{saving ? t('orders.form.saving') : (orderId ? t('orders.form.updateOrder') : t('orders.form.placeOrder'))}</button>
          <button className="flex-1 bg-white border border-green-200 text-green-700 rounded-xl py-2 text-sm" onClick={()=>onClose && onClose(false)}>{t('orders.form.cancelEdit') || 'Cancel'}</button>
        </div>
      </div>
    </div>
  );
}


